import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/** Same breakpoint the PHP pages switched desktop/mobile art on: max-width 768px. */
const MOBILE_QUERY = '(max-width: 768px)';
export function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);
  return mobile;
}

const isMobileDevice = () => /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);

/** stage1.php / flow.php: a phone held sideways gets "Please rotate your device". */
export function useRotateNotice() {
  const check = () => isMobileDevice() && window.innerWidth > window.innerHeight;
  const [show, setShow] = useState(check);
  useEffect(() => {
    const onChange = () => setShow(check());
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);
  return show;
}

// ---- Game clock -------------------------------------------------------------------------------
// The PHP pages resumed the clock from the saved `time` and, after a reload, added the time since
// the page was last opened (the "gettime" cookie). Here the running clock is kept in localStorage
// ({ s: seconds, at: ms }) per player, so it carries on across stages and reloads the same way.
// The landing page clears it, exactly like index.php deleted the cookie.

const timerKey = (s) => (s ? `cringe.timer.${s.userId}.${s.organizationId}.${s.sessionId}` : null);

export function clearGameTimer(session) {
  try {
    const key = timerKey(session);
    if (key) localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

/** "mm:ss" or "mm:ss:ss" (legacy hh:mm:ss) -> seconds. */
export function parseTime(t) {
  const parts = String(t || '').split(':').map((n) => parseInt(n, 10) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

const pad = (n) => String(n).padStart(2, '0');
/** The PHP `target`: "mm:ss" (with the hours in front once past an hour, which PHP parsed back too). */
export function formatTime(total) {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Starts ticking once `active`; `baseTime` is the saved time the stage opens with. */
export function useGameTimer(active, baseTime) {
  const session = useSelector((s) => s.cringe.session);
  const [secs, setSecs] = useState(null);
  const secsRef = useRef(0);

  useEffect(() => {
    if (!active) return undefined;
    const key = timerKey(session);
    let start = parseTime(baseTime);
    try {
      const stored = JSON.parse(localStorage.getItem(key));
      if (stored && Number.isFinite(stored.s)) {
        start = Math.max(start, stored.s + Math.max(0, Math.floor((Date.now() - stored.at) / 1000)));
      }
    } catch {
      /* no stored clock */
    }
    secsRef.current = start;
    setSecs(start);
    const save = () => {
      try {
        localStorage.setItem(key, JSON.stringify({ s: secsRef.current, at: Date.now() }));
      } catch {
        /* storage unavailable */
      }
    };
    save();
    const id = setInterval(() => {
      secsRef.current += 1;
      setSecs(secsRef.current);
      save();
    }, 1000);
    return () => clearInterval(id);
    // baseTime is read once when the stage opens, like the PHP page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, session]);

  return {
    text: formatTime(secs ?? parseTime(baseTime)),
    current: () => formatTime(secsRef.current),
  };
}
